/**
 * Helpers for combination / product upsells in the runtime configurator.
 */

const CONTENT_ONLY_TYPES = new Set(['notice', 'image']);

/**
 * Collect unique upsell product IDs selected for a parent product.
 * Values live on `${productId}-upsells*` fields as ids or `{ id, quantity }`.
 */
export function getSelectedUpsellIds(productId, formData = {}) {
  if (!productId || !formData || typeof formData !== 'object') {
    return [];
  }

  const prefix = `${productId}-upsells`;
  const ids = [];
  const seen = new Set();

  Object.entries(formData).forEach(([key, value]) => {
    if (!key.startsWith(prefix) || !Array.isArray(value)) {
      return;
    }

    value.forEach((item) => {
      const id = item && typeof item === 'object' ? item.id : item;
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
  });

  return ids;
}

/**
 * True when an upsell product has real configurator options (not just content).
 */
export function isConfigurableUpsellProduct(product) {
  const options = product?.options;
  if (!Array.isArray(options) || options.length === 0) {
    return false;
  }

  return options.some((option) => option?.type && !CONTENT_ONLY_TYPES.has(option.type));
}

function normalizeOptionLabel(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/m[²2]/g, 'm2')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isAreaOption(option) {
  return option?.type === 'number' && /\bm2\b/.test(normalizeOptionLabel(option.label));
}

function hasFilledValue(value) {
  return value !== undefined && value !== null && value !== '';
}

/**
 * Copy parent values onto empty nested upsell fields (e.g. parent "20 m²"
 * onto the extra Spanplafond "Aantal m2" field).
 */
export function inheritParentOptionValues(parentProduct, nestedProduct, formData = {}) {
  const parentId = parentProduct?.id;
  const nestedId = nestedProduct?.id;
  if (!parentId || !nestedId || parentId === nestedId) {
    return {};
  }

  const inherited = {};
  const parentOptions = parentProduct.options || [];
  const nestedOptions = nestedProduct.options || [];

  nestedOptions.forEach((nestedOption) => {
    if (!nestedOption?.id) return;

    const nestedField = `${nestedId}-${nestedOption.id}-input`;
    if (hasFilledValue(formData[nestedField])) return;

    const sameLabel = parentOptions.find(
      (option) =>
        option.type === nestedOption.type &&
        normalizeOptionLabel(option.label) === normalizeOptionLabel(nestedOption.label)
    );
    const parentOption =
      sameLabel ||
      (isAreaOption(nestedOption) ? parentOptions.find((option) => isAreaOption(option)) : null);

    if (!parentOption?.id) return;

    const parentValue = formData[`${parentId}-${parentOption.id}-input`];
    if (hasFilledValue(parentValue)) {
      inherited[nestedField] = parentValue;
    }
  });

  return inherited;
}

/**
 * Drop configurable upsells from parent form data so they are not priced
 * as €0 extras on the parent line. Those products are added as own cart items.
 */
export function withoutConfigurableUpsells(formData, productId, configurableIds) {
  const next = { ...(formData || {}) };
  const skip = configurableIds instanceof Set ? configurableIds : new Set(configurableIds || []);

  Object.keys(next).forEach((key) => {
    if (!key.startsWith(`${productId}-upsells`) || !Array.isArray(next[key])) {
      return;
    }

    next[key] = next[key].filter((item) => {
      const id = item && typeof item === 'object' ? item.id : item;
      return !skip.has(id);
    });

    if (next[key].length === 0) {
      delete next[key];
    }
  });

  return next;
}
